import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { determineUserType, hasClientProfile, hasPractitionerProfile, isAdmin } from '@/utils/auth-helpers';

type UserRole = 'client' | 'practitioner' | 'admin' | null;

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  roleReady: boolean;
  activeRole: UserRole;
  signUp: (email: string, password: string, userType: 'client' | 'practitioner', profileData: any) => Promise<{
    error: any | null;
    data: any | null;
  }>;
  signIn: (email: string, password: string, intendedRole?: UserRole) => Promise<{
    error: any | null;
    data: any | null;
  }>;
  signOut: () => Promise<void>;
  getUserType: () => Promise<'client' | 'practitioner' | 'admin' | 'unknown'>;
  setActiveRole: (role: UserRole) => void;
  updatePassword: (newPassword: string) => Promise<{ error: any | null }>;
  resetPassword: (email: string, userType?: 'client' | 'practitioner') => Promise<{ error: any | null }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PASSWORD_RESET_REQUEST_TYPE_KEY = 'password_reset_requested_type';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('AuthProvider rendering, React available:', typeof React);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeRole, setActiveRole] = useState<UserRole>(null);
  const [roleReady, setRoleReady] = useState(false);
  
  // Store intended role persistently during session
  const sessionRoleKey = 'session_active_role';
  
  // Initialize auth state
  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Check if we already have an active role in session storage
          const existingRoleStr = sessionStorage.getItem(sessionRoleKey);
          if (existingRoleStr && existingRoleStr !== 'null') {
            const existingRole = existingRoleStr as UserRole;
            console.log('Using existing session role:', existingRole);
            setActiveRole(existingRole);
            setRoleReady(true);
          } else {
            // Determine user role using localStorage preferred role
            const determineRole = async () => {
              try {
                const preferredRole = localStorage.getItem('auth_intended_role') as UserRole;
                console.log('Using intended role from localStorage:', preferredRole);
                
                // First check if user already has profiles
                const userType = await determineUserType(session.user.id, preferredRole);
                console.log('User type determination result:', userType);
                
                // If user has existing profiles, use them
                if (userType !== 'unknown') {
                  const finalRole = userType as UserRole;
                  setActiveRole(finalRole);
                  setRoleReady(true);
                  
                  // Store role in session storage for persistence
                  sessionStorage.setItem(sessionRoleKey, finalRole);
                  console.log('Found existing user profile, set role:', finalRole);
                } else {
                  // For new users without profiles, use intended role if provided
                  if (preferredRole) {
                    setActiveRole(preferredRole);
                    setRoleReady(true);
                    sessionStorage.setItem(sessionRoleKey, preferredRole);
                    console.log('New user, set intended role:', preferredRole);
                  } else {
                    // No preference and no existing profile - wait for user to choose
                    setActiveRole(null);
                    setRoleReady(true);
                    console.log('No role preference or existing profile found');
                  }
                }
                
                // Clear intended role from localStorage
                localStorage.removeItem('auth_intended_role');
              } catch (error) {
                console.error('Error determining user type:', error);
                localStorage.removeItem('auth_intended_role');
                setActiveRole(null);
                setRoleReady(true);
              }
            };
            determineRole();
          }
        } else {
          setActiveRole(null);
          // Signed-out state is still "ready" for routing decisions.
          // Keeping this false can trap dashboard pages in a loading gate.
          setRoleReady(true);
          localStorage.removeItem('auth_intended_role');
          sessionStorage.removeItem(sessionRoleKey);
        }
        
        setIsLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Check if we already have an active role in session storage
        const existingRoleStr = sessionStorage.getItem(sessionRoleKey);
        if (existingRoleStr && existingRoleStr !== 'null') {
          const existingRole = existingRoleStr as UserRole;
          console.log('Initial session - using existing session role:', existingRole);
          setActiveRole(existingRole);
          setRoleReady(true);
        } else {
          const determineRole = async () => {
            try {
              const preferredRole = localStorage.getItem('auth_intended_role') as UserRole;
              console.log('Initial session - preferred role from localStorage:', preferredRole);
              
              // First check if user already has profiles
              const userType = await determineUserType(session.user.id, preferredRole);
              console.log('Initial session - determined user type:', userType);
              
              // If user has existing profiles, use them
              if (userType !== 'unknown') {
                const finalRole = userType as UserRole;
                setActiveRole(finalRole);
                setRoleReady(true);
                
                // Store role in session storage for persistence
                sessionStorage.setItem(sessionRoleKey, finalRole);
                console.log('Initial session - found existing user profile, set role:', finalRole);
              } else {
                // For new users without profiles, use intended role if provided
                if (preferredRole) {
                  setActiveRole(preferredRole);
                  setRoleReady(true);
                  sessionStorage.setItem(sessionRoleKey, preferredRole);
                  console.log('Initial session - new user, set intended role:', preferredRole);
                } else {
                  // No preference and no existing profile - wait for user to choose
                  setActiveRole(null);
                  setRoleReady(true);
                  console.log('Initial session - no role preference or existing profile found');
                }
              }
              
              // Clear intended role from localStorage
              localStorage.removeItem('auth_intended_role');
            } catch (error) {
              console.error('Error determining user type:', error);
              localStorage.removeItem('auth_intended_role');
              setActiveRole(null);
              setRoleReady(true);
            }
          };
          determineRole();
        }
      } else {
        setActiveRole(null);
        setRoleReady(true);
      }
      
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, userType: 'client' | 'practitioner', profileData: any) => {
    try {
      const redirectUrl = `${window.location.origin}/`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            user_type: userType,
            ...profileData, // Pass all profile data including referral_code_to_apply
          }
        }
      });

      if (error) {
        console.error('SignUp error:', error);
        return { error, data: null };
      }

      // Log consent for terms of service and privacy policy on successful signup
      if (data?.user?.id) {
        const { logConsent } = await import('@/lib/compliance-logger');
        const consentVersion = new Date().toISOString().split('T')[0];
        logConsent({ userId: data.user.id, userType, consentType: 'terms_of_service', consentVersion, action: 'granted' });
        logConsent({ userId: data.user.id, userType, consentType: 'privacy_policy', consentVersion, action: 'granted' });
      }

      return { error: null, data };
    } catch (error) {
      console.error('Unexpected signup error:', error);
      return { error, data: null };
    }
  };

  const signIn = async (email: string, password: string, intendedRole?: UserRole) => {
    try {
      // Set role BEFORE login to ensure it's available during auth state change
      if (intendedRole) {
        localStorage.setItem('auth_intended_role', intendedRole);
        sessionStorage.setItem(sessionRoleKey, intendedRole);
        console.log('Set intended role BEFORE login:', intendedRole);
      }

      // Pre-validate role using the new function to prevent wrong logins
      if (intendedRole) {
        try {
          const { data: roleCheck, error: roleError } = await supabase.rpc(
            'check_user_role_by_email',
            { user_email: email, check_role: intendedRole }
          );

          if (roleError) {
            console.error('Role validation error:', roleError);
            // Continue with login if role check fails
          } else if (roleCheck && typeof roleCheck === 'object') {
            const checkResult = roleCheck as any;
            const userExists = checkResult.user_exists;
            const hasClientProfile = checkResult.has_client_profile;
            const hasPractitionerProfile = checkResult.has_practitioner_profile;

            if (userExists) {
              // If no profile exists yet, don't infer role mismatch here.
              // Let Supabase auth return the real cause (e.g. email not confirmed).
              if (intendedRole === 'client' && !hasClientProfile && hasPractitionerProfile) {
                localStorage.removeItem('auth_intended_role');
                sessionStorage.removeItem(sessionRoleKey);
                return {
                  error: { message: 'This email is not registered as a client account. Please use the practitioner login page or register a new client account.' },
                  data: null
                };
              } else if (intendedRole === 'practitioner' && !hasPractitionerProfile && hasClientProfile) {
                localStorage.removeItem('auth_intended_role');
                sessionStorage.removeItem(sessionRoleKey);
                return {
                  error: { message: 'This email is not registered as a practitioner account. Please use the client login page or register a new practitioner account.' },
                  data: null
                };
              }
            }
          }
        } catch (error) {
          console.error('Error in role pre-validation:', error);
          // Continue with login if pre-validation fails
        }
      }

      // Proceed with actual login
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('SignIn error:', error);
        // Clear role on login failure
        if (intendedRole) {
          localStorage.removeItem('auth_intended_role');
          sessionStorage.removeItem(sessionRoleKey);
        }
        const rawMessage = error.message || '';
        const mappedMessage = /email not confirmed/i.test(rawMessage)
          ? 'Email not confirmed. Please check your inbox and click the verification link before logging in.'
          : 'Invalid email or password.';
        return { error: { message: mappedMessage }, data: null };
      }

      return { error: null, data };
    } catch (error) {
      console.error('Unexpected signin error:', error);
      localStorage.removeItem('auth_intended_role');
      sessionStorage.removeItem(sessionRoleKey);
      return { error, data: null };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('SignOut error:', error);
      }
      setUser(null);
      setSession(null);
      setActiveRole(null);
      // Signed-out users should not block on role resolution.
      setRoleReady(true);
      localStorage.removeItem('auth_intended_role');
      localStorage.removeItem('practitioner_filters'); // Clear persistent filters
      sessionStorage.removeItem(sessionRoleKey);
    } catch (error) {
      console.error('Unexpected signout error:', error);
    }
  };

  const getUserType = async (): Promise<'client' | 'practitioner' | 'admin' | 'unknown'> => {
    if (!user) return 'unknown';
    return await determineUserType(user.id);
  };

  const setActiveRoleHandler = (role: UserRole) => {
    setActiveRole(role);
    // Store the new role in session storage for persistence
    if (role) {
      sessionStorage.setItem(sessionRoleKey, role);
    } else {
      sessionStorage.removeItem(sessionRoleKey);
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      return { error };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email: string, userType?: 'client' | 'practitioner') => {
    try {
      const normalizedUserType = userType === 'practitioner' ? 'practitioner' : 'client';

      // Persist the portal context so recovery callback can stay consistent
      // even if redirect query params are stripped by an intermediate redirect.
      localStorage.setItem(
        PASSWORD_RESET_REQUEST_TYPE_KEY,
        JSON.stringify({
          userType: normalizedUserType,
          ts: Date.now(),
        })
      );
      // Also store in a cross-subdomain cookie so it survives the
      // resonow.org → www.resonow.org redirect (different localStorage origins).
      try {
        document.cookie = `password_reset_type=${normalizedUserType}; domain=.resonow.org; path=/; max-age=3600; SameSite=Lax; Secure`;
      } catch { /* ignore cookie errors */ }

      // Pre-validate role to avoid sending reset links for the wrong portal.
      try {
        const { data: roleCheck, error: roleError } = await supabase.rpc(
          'check_user_role_by_email',
          { user_email: email, check_role: normalizedUserType }
        );

        if (!roleError && roleCheck && typeof roleCheck === 'object') {
          const checkResult = roleCheck as any;
          const userExists = checkResult.user_exists;
          const hasClientProfile = checkResult.has_client_profile;
          const hasPractitionerProfile = checkResult.has_practitioner_profile;

          if (userExists) {
            if (normalizedUserType === 'client' && !hasClientProfile && hasPractitionerProfile) {
              return {
                error: {
                  message: 'This email is registered as a practitioner account. Please use practitioner forgot password.'
                }
              };
            }
            if (normalizedUserType === 'practitioner' && !hasPractitionerProfile && hasClientProfile) {
              return {
                error: {
                  message: 'This email is registered as a client account. Please use client forgot password.'
                }
              };
            }
          }
        }
      } catch (precheckError) {
        // Continue with reset flow if role precheck is unavailable.
        console.warn('resetPassword role precheck failed:', precheckError);
      }

      // Always use the www domain to match Supabase's Redirect URLs whitelist.
      // window.location.origin may return "https://resonow.org" (no www) which
      // is not in the allowed list → Supabase would fall back to Site URL (homepage).
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `https://www.resonow.org/reset-password?type=${normalizedUserType}`
      });

      if (error) {
        localStorage.removeItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
      }

      return { error };
    } catch (error) {
      localStorage.removeItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
      return { error };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    roleReady,
    activeRole,
    signUp,
    signIn,
    signOut,
    getUserType,
    setActiveRole: setActiveRoleHandler,
    updatePassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
